import { AlbatrossIcon, ChevronRightIcon, Typography, tss, useMediaQueries, Avatar } from '@ds/components'
import { useDisabledStyles } from '@components/styles'
import { TCurrentUser, TFreeTrial } from '@types/common'
// import { LogoutMenuItem } from '@components/header/LogoutMenuItem'
import { MenuLink } from '@components/header/OffCanvasMenu'
import { isAdminUser, isAmbassador as memberUtils_isAmbassador, isConsul as memberUtils_isConsul } from '@utils/members'
import { FC, PropsWithChildren, ReactElement, useMemo } from 'react'
import { formatImageUrl } from '@utils/imageSizes'
import { useBreakpointStyles } from '@components/BreakpointStyle'
import EnhancedLink from '@components/EnhancedLink'

const useStyles = tss.create(({ theme }) => ({
    profileItemWrapper: {
        display: 'flex',
        alignItems: 'center',
        lineHeight: 1,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),

        [theme.breakpoints.up('sm')]: {
            width: 320,
        },
    },

    ml1: {
        marginLeft: theme.spacing(1),
    },

    arrowRightIcon: {
        position: 'relative',
        top: 5,
        left: 4,
    },

    guestlistMenuLink: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.background.default,
    },

    menuHeading: {
        padding: '8px',
        background: '#e6e6e6',
    },

    menuUpgradeBadge: {
        display: 'flex',
        alignItems: 'center',
        minHeight: '45px',
        paddingLeft: '8px',
        textTransform: 'none',
        borderBottom: '1px solid #e6e6e6',

        '&.hover': {
            background: '#1e70a1',
            opacity: 0.0,
        },
    },
    cursor__pointer: {
        cursor: 'pointer',
    },

    top__border: {
        borderTop: '1px solid #e6e6e6',
    },
}))

export function MenuHeading({ children }: PropsWithChildren): ReactElement {
    const { classes } = useStyles()
    return <div className={classes.menuHeading}>{children}</div>
}

export const MenuUpgradeBadge: FC<{ route: string; label: string }> = ({ route, label }) => {
    const { classes } = useStyles()

    return (
        <EnhancedLink className={classes.menuUpgradeBadge} to={route} data-testid="nav-menu-item">
            <AlbatrossIcon color="primary.main" />
            <div className={classes.ml1}>
                <label className={classes.cursor__pointer}>{label}</label>
            </div>
            <ChevronRightIcon color="warning.main" />
        </EnhancedLink>
    )
}

const ProfileItem: FC<{ currentUser: TCurrentUser }> = ({ currentUser }) => {
    const { classes } = useStyles()

    if (!currentUser) {
        return null
    }

    return (
        <div className={classes.profileItemWrapper}>
            <Avatar
                src={currentUser.imagePath ? formatImageUrl(currentUser.imagePath, '32_32-3x') : undefined}
                alt={currentUser.fullName}
                size="small"
            />
            <div className={classes.ml1}>
                <Typography variant="h4">{`${currentUser.firstName} ${currentUser.lastName}`}</Typography>
                <Typography variant="subtitle2" color="textSecondary">
                    {currentUser.isUserReadonly ? 'Preview Access' : currentUser.role}
                </Typography>
            </div>
        </div>
    )
}

export type Options = {
    currentUser: TCurrentUser
    freeTrial?: TFreeTrial
    setFlyoutTrigger: (val: HTMLElement | null) => void
    closeMenu?: () => void
    isImpersonated?: boolean
    hardDunning?: boolean
    isRenewal?: boolean
}

export const NavigationMenuItems = ({
    currentUser,
    freeTrial,
    setFlyoutTrigger,
    closeMenu,
    isImpersonated = false,
    hardDunning,
    isRenewal,
}: Options): ReactElement => {
    const { classes, cx } = useStyles()
    const { breakpointDependentValue } = useMediaQueries()
    const subscriptionRefBasic = breakpointDependentValue('ms_ul_mhb', 'he_pf_mem', '')
    const subscriptionRefPremium = breakpointDependentValue('he_ma_ms', 'he_mem', '')
    const isAmbassador = memberUtils_isAmbassador(currentUser.roles)
    const isConsul = memberUtils_isConsul(currentUser.roles)
    const isAdmin = isAdminUser(currentUser.roles)
    const isPrivilegedUser = isAmbassador || isConsul || isAdmin
    const { fullName } = currentUser
    const subscriptionRenewal = '/subscription/renewal'
    const { classes: breakpointClasses } = useBreakpointStyles()
    const { classes: disabledClasses } = useDisabledStyles()

    const disableNavigation = hardDunning && isRenewal

    const disableOnRenewalClass = cx(disableNavigation && disabledClasses.disabled)

    const menuItems = useMemo(() => {
        const items = []

        items.push(
            <MenuLink
                key="he_pr"
                className={disableOnRenewalClass}
                route={
                    hardDunning
                        ? subscriptionRenewal
                        : `https://www.internations.org/profile/${currentUser.id}?ref=he_pr`
                }
                aria-disabled={disableNavigation}
                onClick={closeMenu}
            >
                <ProfileItem currentUser={currentUser} />
            </MenuLink>,
            <MenuLink
                key="he_cn"
                className={disableOnRenewalClass}
                route={
                    hardDunning
                        ? subscriptionRenewal
                        : `https://www.internations.org/profile/${currentUser.id}/contacts?ref=he_cn`
                }
                onClick={closeMenu}
                aria-disabled={disableNavigation}
            >
                contacts
            </MenuLink>,
            <MenuLink
                key="he_if"
                route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/invitation?ref=he_if'}
                onClick={closeMenu}
                className={disableOnRenewalClass}
                aria-disabled={disableNavigation}
            >
                Invite friends
            </MenuLink>,
            <MenuLink
                key="he_fo"
                route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/forum?ref=he_fo'}
                onClick={closeMenu}
                className={cx(disableOnRenewalClass, breakpointClasses.showDesktop)}
                aria-disabled={disableNavigation}
            >
                Forum
            </MenuLink>,
            <MenuLink
                key="he_gu"
                route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/guide?ref=he_gu'}
                onClick={closeMenu}
                className={cx(disableOnRenewalClass, breakpointClasses.showDesktop)}
                aria-disabled={disableNavigation}
            >
                Guide
            </MenuLink>,
            <MenuHeading key="Settings">Settings</MenuHeading>,
        )

        if (currentUser.isPremium) {
            items.push(
                <MenuLink
                    key={`${'membership'}-${subscriptionRefPremium}`}
                    route={'https://www.internations.org/membership?ref=subscriptionRefPremium'}
                    onClick={closeMenu}
                >
                    Membership
                </MenuLink>,
            )
        } else {
            items.push(
                <MenuUpgradeBadge
                    key={`${'upgradeToAlbatross'}-${subscriptionRefBasic}`}
                    route={
                        'https://www.internations.org/subscription?ref=subscriptionRefBasic&upgradeTrigger=ut:my_account/navigation'
                    }
                    label={freeTrial != null ? 'try Albatross for free' : 'Upgrade to Albatross'}
                />,
            )
        }

        items.push(
            <MenuLink
                key="he_po"
                route={hardDunning ? subscriptionRenewal : 'https://www.internations.org/partner-offers?ref=he_po'}
                target="_blank"
                rel="noopener noreferrer"
                className={cx(disableOnRenewalClass, 'hide-for-android-container-app')}
                onClick={closeMenu}
                aria-disabled={disableNavigation}
            >
                Partner Offers
            </MenuLink>,
            <MenuLink
                key="he_lbd"
                route={'https://www.internations.org/account-settings?ref=he_lbd'}
                onClick={closeMenu}
                className="desktop-only"
            >
                Account settings
            </MenuLink>,
            <MenuLink
                className={breakpointClasses.showMobile}
                key="he_ma_ps"
                route={'https://www.internations.org/account-settings?ref=he_ma_ps'}
                onClick={closeMenu}
            >
                Privacy settings
            </MenuLink>,
            <MenuLink
                className={breakpointClasses.showMobile}
                key="he_ma_lbd"
                route={'https://www.internations.org/account-settings?ref=he_ma_lbd'}
                onClick={closeMenu}
            >
                Login & Basic data
            </MenuLink>,
            <MenuLink
                key="he_ma_en"
                route={
                    hardDunning
                        ? subscriptionRenewal
                        : 'https://www.internations.org/account/notifications?ref=he_ma_en'
                }
                onClick={closeMenu}
                className={cx(disableOnRenewalClass, breakpointClasses.showMobile)}
                aria-disabled={disableNavigation}
            >
                Email notifications
            </MenuLink>,
            <MenuHeading key="help">Help</MenuHeading>,
            <MenuLink
                key="he_faq"
                route={'https://support.internations.org/hc/en-us?ref=he_faq'}
                target="_blank"
                onClick={closeMenu}
            >
                FAQ
            </MenuLink>,
            <MenuLink key="he_sp" route={'https://www.internations.org/support/request/?ref=he_sp'} onClick={closeMenu}>
                Request support
            </MenuLink>,
            <MenuLink
                key="he_ab"
                route={'https://www.internations.org/about-internations/?ref=he_ab'}
                onClick={closeMenu}
            >
                About InterNations
            </MenuLink>,
        )

        // ToDo make logout work
        // items.push(<LogoutMenuItem closeMenu={closeMenu} key="logout" />)

        return items
    }, [
        classes.arrowRightIcon,
        classes.guestlistMenuLink,
        closeMenu,
        freeTrial,
        fullName,
        isAdmin,
        isAmbassador,
        isConsul,
        isImpersonated,
        isPrivilegedUser,
        currentUser,
        setFlyoutTrigger,
        subscriptionRefBasic,
        subscriptionRefPremium,
        // canUpgrade,
        hardDunning,
        disableOnRenewalClass,
        cx,
        disableNavigation,
    ])

    return <>{menuItems}</>
}
